import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ExternalRemisions.scss';
import { Button, DataGrid, Form, Tooltip } from 'devextreme-react';
import { Column, Editing, Item, Paging, SearchPanel, Toolbar, Button as Butt, FilterRow, Pager, Lookup, Scrolling } from 'devextreme-react/data-grid';
import { Item as ItemF } from 'devextreme-react/form';
import { editPatient, filter, resButtonRefresh, resGridNotesCheck } from '../../images/icons';
import { useAppSelector } from '../../contexts/hooks';
import { useApiExternalRemisions } from '../../api/api.externalRemisions';
import { FormPopup, newToast, ModalConfirmation } from '../../components';
import { SysI_RemisionesExternas } from '../../types/types.externalRemissions';
import { ModalConfigLaboratories } from './Modals/ModalConfigLaboratories';
import { ModalViewExternalRemision } from './Modals/ModalViewExternalRemision';
import moment from 'moment';

const currentFilters = {
  startDate: moment().format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  id: null as number | null,
};

export default () => {
  const gridRef = useRef<DataGrid>(null);
  // Estados para modales
  const [modalFiltersVisible, setModalFiltersVisible] = useState(false);
  const [modalConfigVisible, setModalConfigVisible] = useState(false);
  const [modalViewVisible, setModalViewVisible] = useState(false);
  const lstLabsRefExt = useAppSelector((state) => state.configuration.Data?.ComboBox?.find((x) => x.Name === 'SysI_RemisionesExternasLaboratorios')?.Data || []);
  
  // Estados para datos
  const [dataSource, setDataSource] = useState<SysI_RemisionesExternas[]>([]);
  const [selectedRemisionId, setSelectedRemisionId] = useState<number | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [selectedLabName, setSelectedLabName] = useState<string>('');
  const [selectedRemisionEstado, setSelectedRemisionEstado] = useState<boolean | undefined>(undefined);
  const [selectedRemisionNota, setSelectedRemisionNota] = useState<string | null>(null);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const pendingDeleteId = useRef<number | null>(null);

  const Prm_ExternalRem_Config = useAppSelector((state) => state.userInfo.Permissions?.Prm_ExternalRem_Config);
  const { getExternalRemisionByFilters, deleteRemisionExterna } = useApiExternalRemisions();

  // Función para cargar datos
  const fetchData = useCallback(async () => {
    gridRef.current?.instance?.beginCustomLoading('Cargando remisiones externas...');
    try {
      const inicio = currentFilters.startDate ? new Date(currentFilters.startDate) : null;
      const fin = currentFilters.endDate ? new Date(currentFilters.endDate) : null;
      const id = currentFilters.id || null;

      const result = await getExternalRemisionByFilters(inicio, fin, id);

      if (result && Array.isArray(result)) {
        setDataSource(result);
      } else {
        setDataSource([]);
      }
    } catch (error) {
      newToast('ERROR', 'Error al cargar las remisiones externas', 4);
      setDataSource([]);
    } finally {
      gridRef.current?.instance?.endCustomLoading();
    }
  }, []);

  // Carga inicial - remisiones del día
  useEffect(() => {
    fetchData();
  }, []);

  // Handlers
  const handleUpdateData = useCallback(() => {
    setModalFiltersVisible(false);
    fetchData();
  }, [fetchData]);

  const handleModalShowFilter = useCallback(() => {
    setModalFiltersVisible((prev) => !prev);
  }, []);

  const handleClicConfigClients = useCallback(() => {
    setModalConfigVisible(true);
  }, []);

  // Handler para ver remisión externa
  const handleView = useCallback((e: any) => {
    try {
      const data = e.row.data as SysI_RemisionesExternas;
      const remisionId = data?.Re_Id;

      if (!data || !remisionId || remisionId <= 0) {
        newToast('ERROR', 'No se pudo obtener los datos de la remisión externa', 4);
        return;
      }

      setSelectedRemisionId(remisionId);
      setSelectedContractId(data.Re_Cn_Id || null);
      setSelectedRemisionEstado(data.Re_Estado);
      setSelectedRemisionNota(data.Re_Nota || null);
      const lab = lstLabsRefExt.find((x: any) => x.Rel_Id === data.Re_Rel_Id);
      setSelectedLabName(lab?.Rel_Nombre || `#${remisionId}`);
      setModalViewVisible(true);
    } catch (error) {
      newToast('ERROR', 'Error al abrir la vista de remisión externa', 4);
    }
  }, []);

  // Cerrar modal de vista
  const handleCloseViewModal = useCallback(() => {
    setModalViewVisible(false);
    setSelectedRemisionId(null);
    setSelectedContractId(null);
    setSelectedLabName('');
    setSelectedRemisionEstado(undefined);
    setSelectedRemisionNota(null);
  }, []);

  // Handler para eliminar remisión externa
  const handleDeleteRemision = useCallback((e: any) => {
    const data = e.row?.data as SysI_RemisionesExternas;
    if (!data?.Re_Id) return;

    if (data.Re_Estado === true) {
      newToast('WARNING', 'No se puede eliminar una remisión que ya fue procesada', 4);
      return;
    }

    pendingDeleteId.current = data.Re_Id;
    setConfirmDeleteVisible(true);
  }, []);

  const handleConfirmDeleteRemision = useCallback(async () => {
    setConfirmDeleteVisible(false);
    const id = pendingDeleteId.current;
    if (!id) return;
    const result = await deleteRemisionExterna(id);
    if (result) {
      setDataSource((prev) => prev.filter((r) => r.Re_Id !== id));
    }
    pendingDeleteId.current = null;
  }, [deleteRemisionExterna]);

  const handleCancelDeleteRemision = useCallback(() => {
    setConfirmDeleteVisible(false);
    pendingDeleteId.current = null;
  }, []);

  // Validar filtros
  const validateForm = useCallback(() => {
    const { startDate, endDate } = currentFilters;
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      newToast('WARNING', 'La fecha inicial no puede ser mayor que la fecha final', 3);
      return false;
    }
    return true;
  }, []);

  // Aplicar filtros
  const handleApplyFilters = useCallback(() => {
    if (!validateForm()) return;
    setModalFiltersVisible(false);
    fetchData();
  }, [validateForm, fetchData]);

  // Renderizado de estado
  const renderEstado = useCallback((cellData: any) => {
    const estado = cellData.value;
    return <span className={`chip ${estado ? 'chip-ok' : 'chip-warn'}`}>{estado ? 'Procesada' : 'Pendiente'}</span>;
  }, []);

  // Renderizado de fecha
  const renderFecha = useCallback((cellData: any) => {
    if (!cellData.value) return <span className='text-muted'>—</span>;
    return <span>{moment(cellData.value).format('DD/MM/YYYY HH:mm')}</span>;
  }, []);

  // Renderizado de nota: ícono estándar de notas cuando la remisión tiene una (con tooltip nativo).
  const renderNota = useCallback((cellData: any) => {
    const nota = cellData.value;
    if (!nota || !String(nota).trim()) return <span className='text-muted'>—</span>;
    return <img src={resGridNotesCheck} alt='Nota' title={String(nota)} style={{ width: 20, height: 20 }} />;
  }, []);

  return (
    <div className={'external-remisions-view'}>
      <div className={'view-wrapper view-wrapper-contact-list'}>
        <DataGrid className='grid' dataSource={dataSource} ref={gridRef} keyExpr='Re_Id' height='100%' width='100%' hoverStateEnabled={true} repaintChangesOnly={true} highlightChanges={false} rowAlternationEnabled={true} showColumnLines={true} columnAutoWidth={true} showBorders={true}>
          <Editing allowUpdating={false} allowDeleting={false} />
          <FilterRow visible={true} applyFilter='auto' />
          <SearchPanel visible placeholder='Buscar remisiones externas...' width={300} />
          <Scrolling mode='virtual' rowRenderingMode='virtual' useNative={true} />
          <Paging enabled={true} />
          <Pager visible={true} allowedPageSizes={[20, 50, 100, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>
                <h2>Remisiones Externas</h2>
              </div>
            </Item>
            <Item location='before' locateInMenu='auto'>
              <div className='separator' />
            </Item>
            <Item location='before' visible={Prm_ExternalRem_Config} locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Tooltip target='#toolBarConfig' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                Configuración de laboratorios externos
              </Tooltip>
              <Button id='toolBarConfig' text='Configuración' stylingMode='outlined' icon={editPatient} type='success' onClick={handleClicConfigClients} />
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Tooltip target='.ext-filter-button' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                Filtrar remisiones externas
              </Tooltip>
              <Button className='ext-filter-button' icon={filter} text='Filtros' stylingMode='text' onClick={handleModalShowFilter} />
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Tooltip target='.ext-refresh-button' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                Actualizar lista
              </Tooltip>
              <Button className='ext-refresh-button' icon={resButtonRefresh} text='Actualizar' stylingMode='text' onClick={handleUpdateData} />
            </Item>
            <Item name='searchPanel' locateInMenu='auto' />
          </Toolbar>

          <Column dataField='Re_Id' caption='ID' alignment='center' width={90} />
          <Column dataField='Re_Rel_Id' caption='LABORATORIO EXTERNO' alignment='left'>
            <Lookup dataSource={lstLabsRefExt} valueExpr='Rel_Id' displayExpr='Rel_Nombre' />
          </Column>
          <Column dataField='Re_FechaCreacion' caption='FECHA CREACIÓN' cellRender={renderFecha} alignment='center' width={170} />
          <Column dataField='Re_FechaProceso' caption='FECHA PROCESO' cellRender={renderFecha} alignment='center' width={170} />
          <Column dataField='Re_Nota' caption='NOTA' cellRender={renderNota} alignment='center' width={80} allowFiltering={false} allowSorting={false} />
          <Column dataField='Re_Estado' caption='ESTADO' cellRender={renderEstado} alignment='center' width={140} />
          <Column type='buttons' caption='ACCIONES' width={120} fixedPosition='right'>
            <Butt icon='eyeopen' text='Ver' hint='Ver pacientes y detalles' onClick={handleView} />
            <Butt icon='trash' text='Eliminar' hint='Eliminar remisión externa' onClick={handleDeleteRemision} visible={(e: any) => e.row?.data?.Re_Estado !== true} />
          </Column>
        </DataGrid>
      </div>

      {/* Modal de filtros */}
      {modalFiltersVisible && (
        <FormPopup title='Filtrar Remisiones Externas' visible={modalFiltersVisible} setVisible={handleModalShowFilter} onSave={handleApplyFilters} loading={false} allowSave={false}>
          <Form formData={currentFilters} labelLocation='top' className='plain-styled-form'>
            <ItemF
              dataField='startDate'
              editorType='dxDateBox'
              label={{ text: 'Fecha inicial' }}
              editorOptions={{
                stylingMode: 'outlined',
                showClearButton: true,
                max: currentFilters.endDate,
              }}
            />
            <ItemF
              dataField='endDate'
              editorType='dxDateBox'
              label={{ text: 'Fecha final' }}
              editorOptions={{
                stylingMode: 'outlined',
                showClearButton: true,
                min: currentFilters.startDate,
              }}
            />
            <ItemF
              dataField='id'
              editorType='dxNumberBox'
              label={{ text: 'ID Remisión' }}
              editorOptions={{
                stylingMode: 'outlined',
                showClearButton: true,
                placeholder: 'Buscar por ID',
              }}
            />
          </Form>
        </FormPopup>
      )}

      {/* Modal de configuración de laboratorios */}
      <ModalConfigLaboratories visible={modalConfigVisible} setVisible={setModalConfigVisible} />

      {/* Modal de vista de remisión externa (pacientes + detalles) */}
      {modalViewVisible && selectedRemisionId && <ModalViewExternalRemision visible={modalViewVisible} remisionId={selectedRemisionId} contractId={selectedContractId} labName={selectedLabName} remisionEstado={selectedRemisionEstado} remisionNota={selectedRemisionNota} onClose={handleCloseViewModal} />}

      {/* Modal de confirmación de eliminación */}
      <ModalConfirmation visible={confirmDeleteVisible} message='Desea eliminar esta remisión externa' onConfirm={handleConfirmDeleteRemision} onCancel={handleCancelDeleteRemision} />
    </div>
  );
};
